import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Check,
  ClipboardList,
  Clock3,
  Compass,
  LayoutList,
  Lock,
  LogIn,
  RotateCcw,
  Sparkles,
} from 'lucide-react';
import SiteBrandedLoader from '../components/SiteBrandedLoader';
import { AssessmentSummaryTiles } from '../components/assessment/AssessmentCharts';
import { AnalysisResult } from '../components/assessment/AnalysisResult';
import { PriorityChart } from '../components/assessment/PriorityChart';
import {
  AssessmentBackdrop,
  BRAND,
  LikertScale,
  HAIRLINE,
  Panel,
  PanelDivider,
  ProgressRing,
  ScaleLegend,
  SCALE_COLORS,
  StepStrip,
  YesNoScale,
} from '../components/assessment/AssessmentUI';
import { useAuth } from '../contexts/AuthContext';
import { assessmentsService } from '../services/api';
import { AssessmentForUser, AssessmentLanding, AssessmentResult } from '../types';
import { buildLoginUrl, currentPathForNext } from '../utils/redirectUtils';
import { formatPersianDate } from '../utils/dateUtils';
import { getCoursePathSegment } from '../utils/coursePath';
import { getImageUrl } from '../utils/imageUtils';
import { AssessmentDraftQueue, DraftStatus } from '../utils/assessmentDraft';

const fa = (n: number) => n.toLocaleString('fa-IR');

type Mode = 'loading' | 'guest' | 'locked' | 'intro' | 'wizard' | 'result';

/**
 * بیرون از کامپوننت تعریف شده — اگر داخل بدنه ساخته شود، در هر رندر یک نوع
 * کامپوننت تازه است و React کل زیردرخت را unmount/remount می‌کند؛ یعنی با هر
 * انتخاب گزینه، انیمیشن و موقعیت اسکرول از بین می‌رفت.
 */
const Shell: React.FC<{ children: React.ReactNode; wide?: boolean }> = ({ children, wide }) => (
  <AssessmentBackdrop>
    <div className={`mx-auto px-4 pb-16 pt-6 sm:px-6 ${wide ? 'max-w-6xl' : 'max-w-3xl'}`}>
      {children}
    </div>
  </AssessmentBackdrop>
);

const ErrorBanner: React.FC<{ message: string }> = ({ message }) => (
  <div className="mb-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-center text-sm font-bold text-red-700">
    {message}
  </div>
);

interface AssessmentProps {
  /**
   * اسلاگ ثابت برای مسیرهای کوتاه اختصاصی (مثل `/zakhm`). پارامتر `slug`/`s`
   * در آدرس همچنان اولویت دارد. بدون هیچ‌کدام، سرور اولین پرسشنامهٔ منتشرشده
   * (مسیر رشد) را برمی‌دارد.
   */
  defaultSlug?: string;
}

const Assessment: React.FC<AssessmentProps> = ({ defaultSlug }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { code } = useParams<{ code: string }>();
  const roundCode = code || searchParams.get('round') || undefined;
  const requestedAttempt = searchParams.get('attempt') || undefined;
  const { user, loading: authLoading } = useAuth();

  /** `s` و `i` نسخهٔ کوتاه `slug` و `invite` هستند تا لینک پیامک کوتاه‌تر شود */
  const slugParam =
    searchParams.get('slug') || searchParams.get('s') || defaultSlug || undefined;
  const workshopParam = searchParams.get('w') || undefined;
  const inviteParam = searchParams.get('invite') || searchParams.get('i') || undefined;
  /** از داشبورد با «پاسخ‌دهی مجدد» آمده؛ به‌جای نتیجهٔ قبلی، صفحهٔ شروع را می‌بیند */
  const retakeParam = searchParams.get('retake') === '1';

  const [mode, setMode] = useState<Mode>('loading');
  const [error, setError] = useState('');
  const [landing, setLanding] = useState<AssessmentLanding | null>(null);
  const [data, setData] = useState<AssessmentForUser | null>(null);
  const [result, setResult] = useState<AssessmentResult | null>(null);

  const [selectedWorkshopId, setSelectedWorkshopId] = useState<string>('');
  const [attemptId, setAttemptId] = useState<string>('');
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [stepIndex, setStepIndex] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [starting, setStarting] = useState(false);
  const [saveState, setSaveState] = useState<DraftStatus>('saved');
  const [storageAvailable, setStorageAvailable] = useState(true);
  const [restoredDraft, setRestoredDraft] = useState(false);
  const draftQueue = useRef<AssessmentDraftQueue | null>(null);
  const unsubscribeDraft = useRef<(() => void) | null>(null);
  const nextLock = useRef(false);
  const questionRefs = useRef<Record<string, HTMLElement | null>>({});

  // مرجع پایدار لازم است وگرنه هر رندر یک آرایهٔ تازه می‌سازد و useMemoهای پایین بی‌اثر می‌شوند
  const sections = useMemo(() => data?.sections ?? [], [data]);
  const currentSection = sections[stepIndex];

  /**
   * با عوض شدن بخش، صفحه باید از سوال اول شروع شود.
   * اسکرول در همان لحظهٔ کلیک کار نمی‌کند: بخش تازه هنوز رندر نشده و
   * اسکرول نرم وسط تعویض محتوا لغو می‌شود. پس بعد از رندر و بدون انیمیشن.
   */
  useLayoutEffect(() => {
    if (mode !== 'wizard') return;
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [stepIndex, mode]);

  const totalQuestions = useMemo(
    () => sections.reduce((sum, s) => sum + s.questions.length, 0),
    [sections],
  );
  const answeredCount = useMemo(
    () => sections.flatMap((s) => s.questions).filter((q) => answers[q.id]).length,
    [sections, answers],
  );
  const progress = totalQuestions ? Math.round((answeredCount / totalQuestions) * 100) : 0;

  const sectionsCompleted = useMemo(
    () => sections.map((s) => s.questions.every((q) => !!answers[q.id])),
    [sections, answers],
  );
  const sectionComplete = sectionsCompleted[stepIndex] ?? false;
  const remainingInSection = currentSection
    ? currentSection.questions.filter((q) => !answers[q.id]).length
    : 0;

  // ── بارگذاری ───────────────────────────────────────────────────────────────

  const loadGuestLanding = useCallback(async () => {
    try {
      setLanding(await assessmentsService.getLanding(slugParam, roundCode));
      setMode('guest');
    } catch (err: any) {
      setError(err?.response?.data?.message || 'پرسشنامهٔ فعالی برای نمایش وجود ندارد.');
      setMode('guest');
    }
  }, [slugParam, roundCode]);

  const loadForUser = useCallback(
    async (workshopId?: string) => {
      const payload = await assessmentsService.getForUser(
        slugParam,
        workshopId,
        inviteParam,
        workshopParam,
        roundCode,
        requestedAttempt,
      );
      setData(payload);
      const attempt = payload.attempt;
      const key = user?.id && attempt ? `assessment-draft:v1:${encodeURIComponent(user.id)}:${encodeURIComponent(attempt.id)}` : '';
      if (draftQueue.current?.key !== key) {
        unsubscribeDraft.current?.(); draftQueue.current?.dispose(); draftQueue.current = null;
        if (attempt && user?.id) {
          let storage: Storage | undefined;
          try { storage = window.localStorage; } catch { /* Report limited browser storage below. */ }
          const queue = new AssessmentDraftQueue({ userId: user.id, attemptId: attempt.id,
            questionIds: payload.sections.flatMap(section => section.questions.map(question => question.id)),
            serverAnswers: attempt.answers, storage, online: () => navigator.onLine,
            save: batch => assessmentsService.saveAnswers(attempt.id, batch),
          });
          draftQueue.current = queue;
          const update = () => { const state = queue.getSnapshot(); setAnswers(state.answers); setSaveState(state.status); setStorageAvailable(!!storage && state.storageAvailable); setRestoredDraft(state.restored || Object.keys(attempt.answers).length > 0); };
          unsubscribeDraft.current = queue.subscribe(update); update(); queue.schedule();
          const firstIncomplete = payload.sections.findIndex(section => section.questions.some(question => !queue.getSnapshot().answers[question.id]));
          setStepIndex(Math.max(0, firstIncomplete));
        } else { setAnswers({}); setRestoredDraft(false); }
      }
      setAttemptId(payload.attempt?.id ?? '');
      // کارگاه هدف را سرور تعیین می‌کند (لینک، دعوت‌نامه، پیش‌نویس، تنها کارگاه فعال)
      setSelectedWorkshopId(
        workshopId ?? (payload.attempt?.workshopId || payload.targetWorkshopId || ''),
      );
      return payload;
    },
    [slugParam, workshopParam, inviteParam, roundCode, requestedAttempt, user?.id],
  );

  useEffect(() => {
    if (authLoading) return;

    let cancelled = false;
    (async () => {
      if (!user) {
        await loadGuestLanding();
        return;
      }
      try {
        const payload = await loadForUser();
        if (cancelled) return;

        // تلاش ناتمام همیشه بر نمایش نتیجهٔ قبلی اولویت دارد
        if (payload.attempt) {
          setMode('wizard');
        } else if (
          payload.hasCompletedAttempt &&
          // برای کارگاه جدید باید از نو پاسخ دهد، نه اینکه نتیجهٔ دورهٔ قبلی را ببیند
          !payload.needsNewAttemptForWorkshop &&
          !(retakeParam && payload.assessment.allowRetake)
        ) {
          setResult(await assessmentsService.getMyResult(payload.assessment.slug, roundCode));
          if (!cancelled) setMode('result');
        } else {
          setMode('intro');
        }
      } catch (err: any) {
        if (cancelled) return;
        // ۴۰۳ یعنی پرسشنامه دعوت‌نامه‌ای است و این کاربر دعوت معتبری ندارد
        if (err?.response?.status === 403) {
          if (roundCode) {
            try {
              const ownResult = await assessmentsService.getMyResult(slugParam, roundCode);
              if (cancelled) return;
              if (ownResult?.status === 'COMPLETED') { setResult(ownResult); setError(''); setMode('result'); return; }
            } catch { /* A closed round grants no new participation; retain its original access message. */ }
          }
          if (cancelled) return;
          setError(err?.response?.data?.message || 'دسترسی به این پرسشنامه ندارید.');
          setMode('locked');
          return;
        }
        setError(err?.response?.data?.message || 'دریافت پرسشنامه انجام نشد.');
        setMode('intro');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [authLoading, user, loadForUser, loadGuestLanding, retakeParam, roundCode, slugParam]);

  // ── ذخیرهٔ خودکار ──────────────────────────────────────────────────────────

  const flushAnswers = useCallback(() => draftQueue.current?.flush() ?? Promise.resolve(), []);

  /** بعد از پاسخ، نرم به اولین سوال بی‌جواب همان بخش می‌رویم تا جریان کند نشود */
  const scrollToNextUnanswered = useCallback(
    (justAnsweredId: string, nextAnswers: Record<string, number>) => {
      if (!currentSection) return;
      const rest = currentSection.questions.slice(
        currentSection.questions.findIndex((q) => q.id === justAnsweredId) + 1,
      );
      const target = rest.find((q) => !nextAnswers[q.id]);
      const node = target ? questionRefs.current[target.id] : null;
      if (!node) return;
      window.setTimeout(() => {
        node.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 180);
    },
    [currentSection],
  );

  const queueAnswer = useCallback(
    (questionId: string, value: number) => {
      if (nextLock.current) return;
      const queue = draftQueue.current;
      if (!queue) return;
      queue.answer(questionId, value);
      scrollToNextUnanswered(questionId, queue.getSnapshot().answers);
    },
    [scrollToNextUnanswered],
  );

  useEffect(() => {
    const connection = () => draftQueue.current?.connectionChanged();
    window.addEventListener('online', connection); window.addEventListener('offline', connection);
    return () => { window.removeEventListener('online', connection); window.removeEventListener('offline', connection); unsubscribeDraft.current?.(); draftQueue.current?.dispose(); };
  }, []);

  // ── اکشن‌ها ────────────────────────────────────────────────────────────────

  const handleStart = async () => {
    if (!data || starting) return;
    setError('');
    setStarting(true);
    try {
      const attempt = await assessmentsService.startAttempt(
        data.assessment.id,
        // وقتی انتخاب کارگاه نمایش داده شده، انتخاب کاربر صریح فرستاده می‌شود
        // (رشتهٔ خالی = بدون کارگاه)؛ وگرنه سرور تنها کارگاه فعال را می‌گذارد
        data.workshops.length ? selectedWorkshopId : undefined,
        inviteParam,
        roundCode,
      );
      // بخش‌های ویژهٔ کارگاه انتخابی تازه اینجا به لیست اضافه می‌شوند؛ کارگاهِ خودِ
      // تلاش فرستاده می‌شود تا سرور همین تلاش را (نه هدف دیگری را) برگرداند
      await loadForUser(attempt.workshopId ?? '');
      setAttemptId(attempt.id);
      setMode('wizard');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err: any) {
      setError(err?.response?.data?.message || 'شروع پرسشنامه انجام نشد.');
      if (err?.response?.status === 403) setMode('locked');
    } finally {
      setStarting(false);
    }
  };

  const handleNext = async () => {
    if (nextLock.current) return;
    if (stepIndex < sections.length - 1) {
      void flushAnswers().catch(() => {});
      setStepIndex((i) => i + 1);
      return;
    }
    nextLock.current = true;
    setSubmitting(true);
    setError('');
    const queue = draftQueue.current;
    try {
      if (!queue) throw new Error('no-open-draft');
      try { await queue.flush(); } catch { setError('همه پاسخ‌ها هنوز در سرور ذخیره نشده‌اند. اتصال اینترنت را بررسی کنید و دوباره ذخیره و ثبت کنید.'); return; }
      if (draftQueue.current !== queue) return;
      setResult(await assessmentsService.submit(attemptId));
      queue.complete();
      setMode('result');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err: any) {
      setError(err?.response?.data?.message || 'ثبت نهایی انجام نشد.');
    } finally {
      nextLock.current = false;
      setSubmitting(false);
    }
  };

  const handleRetake = async () => {
    setError('');
    try {
      await loadForUser(selectedWorkshopId || undefined);
      setResult(null);
      setAnswers({});
      setAttemptId('');
      setStepIndex(0);
      setMode('intro');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err: any) {
      setError(err?.response?.data?.message || 'شروع مجدد انجام نشد.');
    }
  };

  // ── نماها ──────────────────────────────────────────────────────────────────

  if (authLoading || mode === 'loading') {
    return (
      <div className="min-h-screen bg-[#F7F9FF]">
        <SiteBrandedLoader />
      </div>
    );
  }

  // ── مهمان: لندینگ معرفی + دعوت به ورود ─────────────────────────────────────

  if (mode === 'guest') {
    const loginUrl = buildLoginUrl(currentPathForNext(location));
    const workshopTitle = landing?.workshops.find((w) => w.slug === workshopParam)?.title;

    const isAnalysis = landing?.resultMode === 'ANALYSIS';
    const steps = [
      { icon: <LogIn className="h-4 w-4" />, title: 'ورود به حساب', text: 'با شماره موبایل، در چند ثانیه' },
      { icon: <ClipboardList className="h-4 w-4" />, title: 'پاسخ به سوال‌ها', text: 'بخش‌به‌بخش، با ذخیرهٔ خودکار' },
      isAnalysis
        ? { icon: <Sparkles className="h-4 w-4" />, title: 'تحلیل اختصاصی شما', text: 'شدت هر محور + تحلیل حرفه‌ای' }
        : { icon: <Compass className="h-4 w-4" />, title: 'نقشهٔ مسیر شما', text: 'دوره‌ها به ترتیب نیاز واقعی' },
    ];

    return (
      <Shell>
        {error ? <ErrorBanner message={error} /> : null}
        {landing ? (
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: 'easeOut' }}
            className="space-y-4"
          >
            <Panel className="overflow-hidden">
              <div
                className="relative px-5 py-9 text-center text-white sm:px-10 sm:py-12"
                style={{
                  background:
                    'linear-gradient(140deg, #16307F 0%, #1C3B9B 46%, #4B2FA8 100%)',
                }}
              >
                <div
                  aria-hidden
                  className="pointer-events-none absolute inset-0 opacity-70"
                  style={{
                    background:
                      'radial-gradient(420px 200px at 82% 0%, rgba(255,255,255,0.20), transparent 65%)',
                  }}
                />
                <div className="relative">
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-white/25 bg-white/12 px-3 py-1 text-[11px] font-black backdrop-blur-sm">
                    <Sparkles className="h-3.5 w-3.5" />
                    {workshopTitle || (isAnalysis ? 'خودشناسی رایگان' : 'ارزیابی رایگان')}
                  </span>
                  <h1 className="mt-4 text-2xl font-black leading-tight sm:text-[32px]">
                    {landing.title}
                  </h1>
                  {landing.description ? (
                    <p className="mx-auto mt-3 max-w-xl text-[13.5px] leading-8 text-white/85">
                      {landing.description}
                    </p>
                  ) : null}
                </div>
              </div>

              <div
                className="grid grid-cols-3 divide-x divide-x-reverse border-t"
                style={{ borderColor: HAIRLINE, ['--tw-divide-opacity' as any]: 1 }}
              >
                {[
                  { icon: <LayoutList className="h-4 w-4" />, label: 'بخش', value: fa(landing.sectionCount) },
                  { icon: <ClipboardList className="h-4 w-4" />, label: 'سوال', value: fa(landing.questionCount) },
                  { icon: <Clock3 className="h-4 w-4" />, label: 'دقیقه', value: fa(landing.estimatedMinutes) },
                ].map((tile) => (
                  <div
                    key={tile.label}
                    className="px-3 py-5 text-center"
                    style={{ borderColor: HAIRLINE }}
                  >
                    <span className="mx-auto flex h-9 w-9 items-center justify-center text-[#1C3B9B]">
                      {tile.icon}
                    </span>
                    <p className="mt-1.5 text-2xl font-black text-slate-900">{tile.value}</p>
                    <p className="mt-0.5 text-[11px] font-bold text-slate-400">{tile.label}</p>
                  </div>
                ))}
              </div>
            </Panel>

            <Panel className="p-5 sm:p-6">
              <p className="mb-3 text-[13px] font-black text-slate-800">چطور کار می‌کند</p>
              <div className="grid gap-3 sm:grid-cols-3">
                {steps.map((step, index) => (
                  <div
                    key={step.title}
                    className="rounded-2xl border p-4 transition hover:-translate-y-0.5"
                    style={{ borderColor: HAIRLINE }}
                  >
                    <div className="flex items-center gap-2">
                      <span
                        className="flex h-9 w-9 items-center justify-center rounded-xl border text-[#1C3B9B]"
                        style={{ borderColor: HAIRLINE }}
                      >
                        {step.icon}
                      </span>
                      <span className="text-[11px] font-black text-slate-300">
                        {fa(index + 1)}
                      </span>
                    </div>
                    <p className="mt-2.5 text-[13px] font-black text-slate-900">{step.title}</p>
                    <p className="mt-0.5 text-[11.5px] font-bold leading-6 text-slate-500">
                      {step.text}
                    </p>
                  </div>
                ))}
              </div>

              {landing.introText ? (
                <p
                  className="mt-4 rounded-2xl border px-4 py-3.5 text-[12.5px] leading-8 text-slate-600"
                  style={{ borderColor: HAIRLINE }}
                >
                  {landing.introText}
                </p>
              ) : null}

              {landing.sectionTitles.length ? (
                <div className="mt-5">
                  <p className="mb-2 text-[12px] font-black text-slate-700">
                    محورهایی که بررسی می‌شود
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {landing.sectionTitles.map((title) => (
                      <span
                        key={title}
                        className="rounded-full border bg-white px-2.5 py-1 text-[11px] font-bold text-slate-600"
                        style={{ borderColor: HAIRLINE }}
                      >
                        {title}
                      </span>
                    ))}
                  </div>
                </div>
              ) : null}
            </Panel>

            <Panel className="overflow-hidden">
              <div className="p-5 text-center sm:p-7">
                <span
                  className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl border text-[#1C3B9B]"
                  style={{ borderColor: HAIRLINE }}
                >
                  <Lock className="h-5 w-5" />
                </span>
                <p className="mt-3 text-[15px] font-black text-slate-900">
                  برای شرکت در پرسشنامه ابتدا وارد شوید
                </p>
                <p className="mx-auto mt-1.5 max-w-md text-[12px] font-bold leading-7 text-slate-500">
                  بلافاصله بعد از ورود به همین صفحه برمی‌گردید. پاسخ‌ها روی حساب شما ذخیره
                  می‌شود، پس می‌توانید نیمه‌کاره رها کنید و بعداً ادامه دهید.
                </p>
                {landing.inviteOnly && !inviteParam ? (
                  <p className="mx-auto mt-2 max-w-md rounded-xl border border-amber-200 px-3 py-2 text-[11.5px] font-black leading-6 text-amber-700">
                    این پرسشنامه فقط با لینک دعوت باز می‌شود. اگر لینک را از ما گرفته‌اید،
                    حتماً روی همان لینک کامل کلیک کنید.
                  </p>
                ) : null}
                <button
                  type="button"
                  onClick={() => navigate(loginUrl)}
                  className="group mt-4 inline-flex items-center gap-2 rounded-2xl px-7 py-3 text-sm font-black text-white shadow-[0_18px_34px_-18px_rgba(28,59,155,0.9)] transition hover:-translate-y-0.5"
                  style={{ background: 'linear-gradient(135deg,#1C3B9B,#3A2FA0)' }}
                >
                  <LogIn className="h-4 w-4" />
                  ورود یا ثبت‌نام
                  <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-0.5" />
                </button>
              </div>
            </Panel>
          </motion.div>
        ) : null}
      </Shell>
    );
  }

  // ── بدون دعوت‌نامه ─────────────────────────────────────────────────────────

  if (mode === 'locked') {
    return (
      <Shell>
        <Panel className="overflow-hidden">
          <div
            className="px-5 py-8 text-center text-white sm:px-8"
            style={{ background: 'linear-gradient(140deg,#16307F 0%,#1C3B9B 50%,#4B2FA8 100%)' }}
          >
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl border border-white/25 bg-white/12 backdrop-blur-sm">
              <Lock className="h-6 w-6" />
            </span>
            <h1 className="mt-3 text-xl font-black sm:text-2xl">این پرسشنامه دعوت‌نامه‌ای است</h1>
            <p className="mx-auto mt-2 max-w-md text-[13px] leading-8 text-white/85">
              {error || 'برای شرکت در این پرسشنامه باید لینک اختصاصی آن را دریافت کرده باشید.'}
            </p>
          </div>
          <div className="p-5 text-center sm:p-7">
            <p className="text-[12.5px] font-bold leading-8 text-slate-600">
              اگر لینک را از طریق پیامک یا پشتیبانی دریافت کرده‌اید، روی همان لینک کامل کلیک
              کنید — کد دعوت داخل خود لینک است و با کپی کردن بخشی از آن از بین می‌رود.
            </p>
            <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:justify-center">
              <Link
                to="/dashboard"
                className="rounded-2xl px-5 py-3 text-sm font-black text-white"
                style={{ background: 'linear-gradient(135deg,#1C3B9B,#3A2FA0)' }}
              >
                بازگشت به داشبورد
              </Link>
              <Link
                to="/contact"
                className="rounded-2xl border px-5 py-3 text-sm font-black text-[#1C3B9B]"
                style={{ borderColor: HAIRLINE }}
              >
                تماس با پشتیبانی
              </Link>
            </div>
          </div>
        </Panel>
      </Shell>
    );
  }

  // ── معرفی + انتخاب کارگاه ──────────────────────────────────────────────────

  if (mode === 'intro' && data) {
    return (
      <Shell>
        {error ? <ErrorBanner message={error} /> : null}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, ease: 'easeOut' }}
        >
          <Panel className="overflow-hidden">
            <div
              className="px-5 py-7 text-white sm:px-8"
              style={{ background: 'linear-gradient(140deg,#16307F 0%,#1C3B9B 50%,#4B2FA8 100%)' }}
            >
              <span className="inline-flex items-center gap-1.5 rounded-full border border-white/25 bg-white/12 px-3 py-1 text-[11px] font-black">
                <Sparkles className="h-3.5 w-3.5" />
                آمادهٔ شروع
              </span>
              <h1 className="mt-3 text-2xl font-black sm:text-[28px]">{data.assessment.title}</h1>
              {data.assessment.introText ? (
                <p className="mt-2.5 max-w-2xl text-[13px] leading-8 text-white/85">
                  {data.assessment.introText}
                </p>
              ) : null}
            </div>

            <div className="p-5 sm:p-7">
              {data.needsNewAttemptForWorkshop && data.lastCompleted ? (
                <div className="mb-4 rounded-2xl border border-amber-200 bg-amber-50/70 px-4 py-3 text-[12px] font-bold leading-7 text-amber-800">
                  شما این پرسشنامه را قبلاً{' '}
                  {data.lastCompleted.workshopTitle
                    ? `برای «${data.lastCompleted.workshopTitle}»`
                    : 'بدون انتخاب کارگاه'}{' '}
                  ({formatPersianDate(data.lastCompleted.completedAt)}) تکمیل کرده‌اید. این
                  بار پاسخ شما جداگانه و به نام کارگاهی که در ادامه انتخاب می‌کنید ثبت می‌شود.
                </div>
              ) : null}

              {data.workshops.length ? (
                <>
                  <p className="text-[14px] font-black text-slate-900">
                    پاسخ شما به نام کدام کارگاه ثبت شود؟
                  </p>
                  <p className="mt-1 text-[11.5px] font-bold leading-6 text-slate-500">
                    نتیجهٔ شما در گزارش همان کارگاه دیده می‌شود و بخش‌های ویژهٔ آن (اگر
                    داشته باشد) برایتان نمایش داده می‌شود.
                  </p>
                  {/* رادیو، نه کلید: انتخاب با کلیک دوباره «خاموش» نمی‌شود تا پاسخ بی‌صدا
                      بدون کارگاه ثبت نشود؛ گزینهٔ «هیچ‌کدام» صریح و جداست */}
                  <div role="radiogroup" className="mt-3.5 space-y-2">
                    {[
                      ...data.workshops.map((w) => ({
                        id: w.id,
                        title: w.title,
                        description: w.description,
                        attended: w.attended,
                        muted: false,
                      })),
                      {
                        id: '',
                        title: 'در هیچ‌کدام از این کارگاه‌ها شرکت نکرده‌ام',
                        description: null,
                        attended: false,
                        muted: true,
                      },
                    ].map((option) => {
                      const active = selectedWorkshopId === option.id;
                      return (
                        <button
                          key={option.id || 'none'}
                          type="button"
                          role="radio"
                          aria-checked={active}
                          onClick={() => setSelectedWorkshopId(option.id)}
                          className={`flex w-full items-center gap-3 rounded-2xl border bg-white text-right transition ${
                            option.muted ? 'p-3' : 'p-4'
                          } ${
                            active
                              ? 'border-[#1C3B9B] shadow-[0_14px_30px_-24px_rgba(28,59,155,0.8)]'
                              : 'hover:border-[#9DB0E4]'
                          }`}
                          style={active ? undefined : { borderColor: HAIRLINE }}
                        >
                          <span
                            className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 transition ${
                              active ? 'border-transparent' : 'border-slate-300'
                            }`}
                            style={active ? { backgroundColor: BRAND } : undefined}
                          >
                            {active ? <Check className="h-3.5 w-3.5 text-white" /> : null}
                          </span>
                          <span className="min-w-0 flex-1">
                            <span
                              className={`block font-black ${
                                option.muted
                                  ? 'text-[12px] text-slate-500'
                                  : 'text-[13.5px] text-slate-900'
                              }`}
                            >
                              {option.title}
                            </span>
                            {option.description ? (
                              <span className="mt-0.5 block text-[11.5px] font-bold leading-6 text-slate-500">
                                {option.description}
                              </span>
                            ) : null}
                          </span>
                          {option.attended ? (
                            <span className="shrink-0 rounded-full border border-[#BFE6BF] px-2 py-1 text-[10px] font-black text-[#0A6B0A]">
                              شما شرکت کرده‌اید
                            </span>
                          ) : null}
                        </button>
                      );
                    })}
                  </div>
                </>
              ) : null}

              <button
                type="button"
                onClick={handleStart}
                disabled={starting}
                className="group mt-6 flex w-full items-center justify-center gap-2 rounded-2xl px-5 py-3.5 text-sm font-black text-white shadow-[0_18px_34px_-18px_rgba(28,59,155,0.9)] transition hover:-translate-y-0.5 disabled:cursor-wait disabled:opacity-70"
                style={{ background: 'linear-gradient(135deg,#1C3B9B,#3A2FA0)' }}
              >
                {starting ? 'در حال شروع…' : 'شروع پرسشنامه'}
                {!starting ? (
                  <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-0.5" />
                ) : null}
              </button>

              {data.hasCompletedAttempt ? (
                <button
                  type="button"
                  onClick={async () => {
                    setResult(await assessmentsService.getMyResult(data.assessment.slug, roundCode));
                    setMode('result');
                  }}
                  className="mt-2 w-full rounded-2xl border border-[#C8D6FF] bg-white px-5 py-3 text-sm font-black text-[#1C3B9B] transition hover:bg-[#F7FAFF]"
                >
                  مشاهدهٔ نتیجهٔ قبلی
                </button>
              ) : null}
            </div>
          </Panel>
        </motion.div>
      </Shell>
    );
  }

  // ── ویزارد سوال‌ها ─────────────────────────────────────────────────────────

  if (mode === 'wizard' && data && currentSection) {
    const labels = data.assessment.optionLabels;
    const isYesNo = data.assessment.scaleType === 'YES_NO';
    const isLastStep = stepIndex === sections.length - 1;
    const workshopTitle = data.workshops.find((w) => w.id === selectedWorkshopId)?.title;

    return (
      <Shell>
        <div
          className="sticky top-0 z-20 -mx-4 mb-4 border-b bg-[#FAFBFE]/88 px-4 py-3 backdrop-blur-md sm:-mx-6 sm:px-6"
          style={{ borderColor: HAIRLINE }}
        >
          <div className="flex items-center gap-3">
            <ProgressRing value={progress} />
            <div className="min-w-0 flex-1">
              {/* عنوان بخش عمداً نمایش داده نمی‌شود تا پاسخ‌دهنده متوجه
                  موضوع سنجش نشود و پاسخ‌ها سوگیری نگیرند */}
              <p className="truncate text-[13.5px] font-black text-slate-900">
                {data.assessment.title}
              </p>
              <p className="mt-0.5 truncate text-[11px] font-bold text-slate-400">
                {data.round ? `${data.round.title} · ` : ''}
                بخش {fa(stepIndex + 1)} از {fa(sections.length)} ·{' '}
                {fa(answeredCount)} از {fa(totalQuestions)} سوال
                {workshopTitle ? ` · ${workshopTitle}` : ''}
              </p>
            </div>
            <span className="shrink-0 text-[10.5px] font-black">
              {saveState === 'saving' ? (
                <span className="text-slate-400">در حال ذخیره…</span>
              ) : saveState === 'saved' ? (
                <span className="inline-flex items-center gap-1 text-emerald-600">
                  <Check className="h-3.5 w-3.5" />
                  در سرور ذخیره شد
                </span>
              ) : <span className="text-amber-700">{saveState === 'offline' ? 'آفلاین' : saveState === 'error' ? 'ذخیره نشده' : 'در انتظار ذخیره'}</span>}
            </span>
          </div>
          <div className="mt-2.5">
            <StepStrip
              sections={sections}
              current={stepIndex}
              completed={sectionsCompleted}
              onJump={(index) => { if (!nextLock.current) setStepIndex(index); }}
            />
          </div>
        </div>

        <div role="status" className="mb-4 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-xs leading-7 text-slate-600">
          {restoredDraft && <p>پاسخ‌های ناتمام شما بازیابی شد؛ می‌توانید از همین بخش ادامه دهید.</p>}
          {saveState === 'offline' || saveState === 'error' || saveState === 'unsaved' ? <p>{storageAvailable ? 'آخرین تغییرات در این مرورگر نگه داشته شده‌اند و هنوز در سرور ذخیره نشده‌اند.' : 'آخرین تغییرات هنوز در سرور ذخیره نشده‌اند؛ ذخیره در مرورگر نیز در دسترس نیست. صفحه را نبندید.'}</p> : <p>پاسخ‌های ذخیره‌شده با ورود دوباره به حساب شما قابل ادامه هستند.</p>}
          {(saveState === 'offline' || saveState === 'error') && <button type="button" disabled={submitting} onClick={() => void flushAnswers().catch(() => {})} className="mt-1 rounded-lg px-3 py-2 font-bold text-[#1C3B9B] focus-visible:ring-2 focus-visible:ring-blue-500">تلاش دوباره برای ذخیره</button>}
          {!storageAvailable && saveState === 'saving' && <p className="text-amber-700">ذخیره مرورگر در دسترس نیست؛ تا تأیید ذخیره در سرور صفحه را نبندید.</p>}
        </div>
        {error ? <ErrorBanner message={error} /> : null}

        <AnimatePresence mode="wait">
          <motion.div
            key={currentSection.id}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            transition={{ duration: 0.24, ease: 'easeOut' }}
            className="space-y-3"
          >
            <Panel className="overflow-hidden">
              <div className="flex items-center gap-4 px-5 py-4">
                <span
                  className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl text-[17px] font-black text-white shadow-[0_12px_24px_-14px_rgba(28,59,155,0.9)]"
                  style={{ background: 'linear-gradient(135deg,#1C3B9B,#3A2FA0)' }}
                >
                  {fa(stepIndex + 1)}
                </span>
                <div className="min-w-0">
                  <h2 className="text-lg font-black text-slate-900 sm:text-xl">
                    بخش {fa(stepIndex + 1)} از {fa(sections.length)}
                  </h2>
                  <p className="mt-0.5 text-[11.5px] font-bold text-slate-400">
                    {fa(currentSection.questions.length)} سوال
                    {remainingInSection
                      ? ` · ${fa(remainingInSection)} سوال باقی مانده`
                      : ' · تکمیل شد'}
                  </p>
                </div>
              </div>
              {currentSection.description ? (
                <>
                  <PanelDivider />
                  <p className="px-5 py-3 text-[12px] font-bold leading-7 text-slate-500">
                    {currentSection.description}
                  </p>
                </>
              ) : null}
              {/* راهنمای طیف فقط برای لیکرت؛ بله/خیر نیازی به توضیح ندارد */}
              {!isYesNo ? (
                <>
                  <PanelDivider />
                  <div className="px-5 py-3.5">
                    <ScaleLegend labels={labels} />
                  </div>
                </>
              ) : null}
            </Panel>

            {currentSection.questions.map((question, qIndex) => {
              const selected = answers[question.id];
              const answerColor = selected ? SCALE_COLORS[selected - 1] : null;
              return (
                <motion.fieldset
                  key={question.id}
                  ref={(node: HTMLFieldSetElement | null) => {
                    questionRefs.current[question.id] = node;
                  }}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.28, delay: Math.min(qIndex * 0.05, 0.3) }}
                  className="relative overflow-hidden rounded-[26px] border bg-white px-4 py-4 shadow-[0_14px_34px_-32px_rgba(15,23,42,0.45)] sm:px-5"
                  style={{ borderColor: answerColor ? `${answerColor}33` : HAIRLINE }}
                >
                  {/* ردِ رنگی پاسخ در لبهٔ کارت — نشان می‌دهد کدام درجه انتخاب شده */}
                  {answerColor ? (
                    <span
                      aria-hidden
                      className="absolute inset-y-0 right-0 w-[3px]"
                      style={{ backgroundColor: answerColor }}
                    />
                  ) : null}
                  <legend className="sr-only">{question.text}</legend>
                  <div className="mb-4 flex items-start gap-2.5">
                    <span
                      className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-lg border text-[11px] font-black transition"
                      style={
                        answerColor
                          ? { backgroundColor: answerColor, borderColor: answerColor, color: '#fff' }
                          : { borderColor: HAIRLINE, color: '#94A3B8' }
                      }
                    >
                      {selected ? <Check className="h-3.5 w-3.5" /> : fa(qIndex + 1)}
                    </span>
                    <p className="text-[14.5px] font-bold leading-8 text-slate-800">
                      {question.text}
                    </p>
                  </div>

                  <fieldset disabled={submitting} className="min-w-0">
                  {isYesNo ? (
                    <YesNoScale
                      name={question.text}
                      value={selected}
                      onChange={(value) => queueAnswer(question.id, value)}
                    />
                  ) : (
                    <LikertScale
                      name={question.text}
                      labels={labels}
                      value={selected}
                      onChange={(value) => queueAnswer(question.id, value)}
                    />
                  )}
                  </fieldset>
                </motion.fieldset>
              );
            })}
          </motion.div>
        </AnimatePresence>

        <div
          className="sticky bottom-0 z-20 -mx-4 mt-5 border-t bg-[#FAFBFE]/90 px-4 py-3 backdrop-blur-md sm:-mx-6 sm:px-6"
          style={{ borderColor: HAIRLINE }}
        >
          <div className="flex items-center gap-2.5">
            <button
              type="button"
              disabled={stepIndex === 0 || submitting}
              onClick={() => {
                setStepIndex((i) => Math.max(0, i - 1));
              }}
              className="flex shrink-0 items-center gap-1.5 rounded-2xl border border-[#C8D6FF] bg-white px-4 py-3 text-sm font-black text-[#1C3B9B] transition hover:bg-[#F4F7FF] disabled:opacity-40 disabled:hover:bg-white"
            >
              <ArrowRight className="h-4 w-4" />
              <span className="hidden sm:inline">قبلی</span>
            </button>

            <button
              type="button"
              disabled={!sectionComplete || submitting}
              onClick={handleNext}
              className="group flex flex-1 items-center justify-center gap-2 rounded-2xl px-4 py-3 text-sm font-black text-white shadow-[0_16px_30px_-18px_rgba(28,59,155,0.9)] transition disabled:cursor-not-allowed disabled:opacity-45 disabled:shadow-none"
              style={{
                background: sectionComplete
                  ? 'linear-gradient(135deg,#1C3B9B,#3A2FA0)'
                  : '#94A3B8',
              }}
            >
              {submitting
                ? 'در حال ثبت…'
                : !sectionComplete
                  ? `${fa(remainingInSection)} سوال باقی مانده`
                  : isLastStep
                    ? 'ثبت نهایی و مشاهدهٔ نتیجه'
                    : 'بخش بعدی'}
              {!submitting && sectionComplete && !isLastStep ? (
                <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-0.5" />
              ) : null}
            </button>
          </div>
        </div>
      </Shell>
    );
  }

  // ── نتیجه: حالت تحلیل (شدت + متن + CTA) ───────────────────────────────────

  if (mode === 'result' && result && result.resultMode === 'ANALYSIS') {
    return (
      <Shell>
        {error ? <ErrorBanner message={error} /> : null}
        <Link to="/dashboard?tab=growth" className="mb-4 inline-block rounded-xl px-3 py-2 text-sm font-bold text-[#1C3B9B]">تاریخچه پرسشنامه‌ها و روند تغییرات</Link>
        <AnalysisResult
          result={result}
          allowRetake={!!data?.assessment.allowRetake && !roundCode}
          onRetake={handleRetake}
        />
      </Shell>
    );
  }

  // ── نتیجه: حالت مسیر رشد (اولویت‌بندی دوره‌ها) ────────────────────────────

  if (mode === 'result' && result) {
    const topThree = result.items.slice(0, 3);

    return (
      <Shell wide>
        {error ? <ErrorBanner message={error} /> : null}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
          className="space-y-4"
        >
          <Panel className="overflow-hidden">
            <div
              className="px-5 py-8 text-center text-white sm:px-8"
              style={{ background: 'linear-gradient(140deg,#16307F 0%,#1C3B9B 50%,#4B2FA8 100%)' }}
            >
              <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl border border-white/25 bg-white/12 backdrop-blur-sm">
                <CheckCircle2 className="h-7 w-7" />
              </span>
              <h1 className="mt-3 text-2xl font-black sm:text-[28px]">
                نقشهٔ مسیر رشد شما آماده است
              </h1>
              <p className="mx-auto mt-2 max-w-xl text-[13px] leading-8 text-white/85">
                دوره‌ها بر اساس نیاز واقعی شما مرتب شده‌اند — هرچه امتیاز یک بخش کمتر باشد،
                دیدن آن دوره برای شما فوری‌تر است.
              </p>
              {result.workshop || result.completedAt ? (
                <p className="mt-2 text-[11px] font-black text-white/70">
                  {result.workshop ? `ثبت‌شده به نام «${result.workshop.title}»` : ''}
                  {result.workshop && result.completedAt ? ' · ' : ''}
                  {result.completedAt ? formatPersianDate(result.completedAt) : ''}
                </p>
              ) : null}
            </div>
            <div className="p-4 sm:p-5">
              <AssessmentSummaryTiles {...result.summary} />
            </div>
          </Panel>

          {topThree.some((i) => i.course) ? (
            <Panel className="p-4 sm:p-5">
              <p className="mb-3 text-[14px] font-black text-slate-900">
                پیشنهاد می‌کنیم از این دوره‌ها شروع کنید
              </p>
              <div className="grid gap-3 sm:grid-cols-3">
                {topThree
                  .filter((item) => item.course)
                  .map((item, index) => (
                    <Link
                      key={item.sectionId}
                      to={`/courses/${getCoursePathSegment(item.course!)}`}
                      className="group overflow-hidden rounded-2xl border bg-white transition hover:-translate-y-1 hover:border-[#9DB0E4] hover:shadow-[0_22px_40px_-28px_rgba(28,59,155,0.6)]"
                      style={{ borderColor: HAIRLINE }}
                    >
                      <div className="relative h-32 overflow-hidden">
                        {item.course!.thumbnail ? (
                          <img
                            src={getImageUrl(item.course!.thumbnail) || ''}
                            alt={item.course!.title}
                            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                          />
                        ) : null}
                        <span className="absolute right-2 top-2 rounded-full bg-white/95 px-2.5 py-1 text-[10px] font-black text-[#1C3B9B] shadow-sm">
                          اولویت {fa(index + 1)}
                        </span>
                      </div>
                      <div className="p-3.5">
                        <p className="text-[10.5px] font-black text-slate-400">{item.title}</p>
                        <p className="mt-1 line-clamp-2 text-[13.5px] font-black leading-7 text-slate-900">
                          {item.course!.title}
                        </p>
                        <p className="mt-2 text-[11px] font-black text-[#1C3B9B]">
                          امتیاز شما: {fa(item.score25)} از {fa(25)}
                        </p>
                      </div>
                    </Link>
                  ))}
              </div>
            </Panel>
          ) : null}

          <Panel className="p-4 sm:p-6">
            <p className="text-[15px] font-black text-slate-900">نقشهٔ اولویت‌ها</p>
            <p className="mt-1 max-w-2xl text-[11.5px] font-bold leading-7 text-slate-500">
              امتیاز هر بخش روی محور ۰ تا ۲۵. هرچه نقطه راست‌تر باشد، آن بخش برای شما
              فوری‌تر است. روی هر ردیف بزنید تا به دورهٔ مربوطه بروید.
            </p>
            <div className="mt-4">
              <PriorityChart
                items={result.items}
                onOpenCourse={(item) =>
                  item.course && navigate(`/courses/${getCoursePathSegment(item.course)}`)
                }
              />
            </div>
          </Panel>

          <div className="flex flex-col gap-2.5 sm:flex-row">
            <button
              type="button"
              onClick={() => navigate('/dashboard?tab=growth')}
              className="flex-1 rounded-2xl px-4 py-3.5 text-sm font-black text-white shadow-[0_16px_30px_-18px_rgba(28,59,155,0.9)] transition hover:-translate-y-0.5"
              style={{ background: 'linear-gradient(135deg,#1C3B9B,#3A2FA0)' }}
            >
              مشاهده در داشبورد
            </button>
            {/* وقتی پرسشنامه یک‌بارمصرف است، سرور هم تلاش دوم را رد می‌کند؛
                پس دکمه هم نباید دیده شود */}
            {data?.assessment.allowRetake && !roundCode ? (
              <button
                type="button"
                onClick={handleRetake}
                className="flex items-center justify-center gap-1.5 rounded-2xl border border-[#C8D6FF] bg-white px-5 py-3.5 text-sm font-black text-[#1C3B9B] transition hover:bg-[#F4F7FF]"
              >
                <RotateCcw className="h-4 w-4" />
                پاسخ‌دهی مجدد
              </button>
            ) : null}
          </div>
        </motion.div>
      </Shell>
    );
  }

  return (
    <Shell>
      {error ? <ErrorBanner message={error} /> : null}
      <Panel className="p-10 text-center">
        <p className="text-sm font-bold text-slate-500">
          در حال حاضر پرسشنامهٔ فعالی برای نمایش وجود ندارد.
        </p>
        <Link
          to="/dashboard"
          className="mt-4 inline-block rounded-2xl px-5 py-3 text-sm font-black text-white"
          style={{ background: 'linear-gradient(135deg,#1C3B9B,#3A2FA0)' }}
        >
          بازگشت به داشبورد
        </Link>
      </Panel>
    </Shell>
  );
};

export default Assessment;
